import { getEnv } from 'utils/env';

import { includes } from 'lodash';

import { SET_FETCHING_SITE_ROLE } from 'apps/core/site_options/store';

import { uglyDispatch } from 'store/utils';
import { getCookie } from 'utils/cookies';

export const API_ROOT = getEnv('REACT_APP_API_ROOT', 'http://localhost:8000/api/');
export const API_AUTH_URL = `${API_ROOT}/api-login/`;
export const API_LOGOUT_URL = `${API_ROOT}/api-logout/`;

const API_AUTH_INVALID = [
    'Signature has expired.',
    'Authentication credentials were not provided.',
    'Error decoding signature.',
];

// Custom Error
export class APIError extends Error {
    constructor(message, extras = {}) {
        super(message);
        this.name = 'APIError';
        this.json = extras.json || {};
        this.status = extras.status;
    }
}

// utility function for setting up init object to fetch
export const fetchInit = meta => {
    meta = meta || {};
    const passedHeaders = meta.headers || {};

    // See https://developer.mozilla.org/en-US/docs/Web/API/GlobalFetch/fetch
    const defaults = {
        method: 'GET',
        mode: 'same-origin',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer-when-downgrade',
    };

    const defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    const csrfToken = getCookie('csrftoken');
    if (csrfToken) {
        defaultHeaders['X-CSRFToken'] = csrfToken;
    }

    // if we pass null explicitly, then don't set Content-Type
    // at all and browser will do it automatically
    // (annoyingly, null/undefined/'' would just set it to 'null', etc.
    if (passedHeaders['Content-Type'] === null) {
        delete defaultHeaders['Content-Type'];
        delete passedHeaders['Content-Type'];
    }

    const headers = { ...defaultHeaders, ...passedHeaders };
    return { ...defaults, ...meta, headers };
};

export function apiUrl(url = '') {
    if (!url.startsWith('http')) {
        return `${API_ROOT}/${url}`;
    } else {
        return url;
    }
}

const handle403 = json => {
    // A 403 ("Forbidden") can be because the user lacks
    // a valid token (i.e. they need to login) or b/c they
    // simply lack permission to do something. We want to handle
    // the former only and raise an APIError for the rest.
    if (includes(API_AUTH_INVALID, json.detail)) {
        uglyDispatch({ type: 'LOGIN_ERROR' });
        uglyDispatch({ type: SET_FETCHING_SITE_ROLE });
    }

    throw new APIError('Forbidden', { json, status: 403 });
};

const handle413 = () => {
    throw new APIError('File too large', {
        json: { detail: 'File too large' },
        status: 413,
    });
};

const handle429 = json => {
    throw new APIError('Forbidden', { json, status: 429 });
};

const handle50x = (status, detail) => {
    const json = { detail };
    throw new APIError('Server Error', { json, status });
};

const handleBadJSON = (error, originalStatus) => {
    const json = {
        originalStatus,
        detail: 'Invalid response from server',
    };
    // server shouldn't ever return non-json! treat like a server error
    const status = 500;
    throw new APIError('Invalid API Response', { json, status });
};

const handleFailedRequests = error => {
    if (error.name === 'TypeError') {
        uglyDispatch({
            type: 'API_STATUS_DOWN',
            message:
                'The network is not available. This could mean that internet access is down, or that the api service is not currently available.',
        });
        throw new APIError('API Inaccessible', { status: 0 });
    } else if (error.name === 'AbortError') {
        return null; // Do nothing
    } else {
        /// TODO ???
    }
};

const doFetch = async (url, init = {}) => {
    const response = await fetch(url, init).catch(handleFailedRequests);
    const status = await response.status;
    const statusText = response.statusText;

    // success w/o content
    if (status === 204) {
        return {};
    }

    // success with content
    if (status >= 200 && status < 300) {
        return await response.json().catch(handleBadJSON, status);
    }

    if (status === 403) {
        const json = await response.json().catch(handleBadJSON, status);
        return handle403(json);
    }

    if (status === 413) {
        return handle413();
    }

    if (status === 429) {
        const json = await response.json().catch(handleBadJSON, status);
        return handle429(json);
    }

    if (status >= 500) {
        return handle50x(status, statusText);
    }

    const json = await response.json().catch(handleBadJSON, status);
    throw new APIError(response.statusText, { json, status });
};

// fetch wrapper methods
// - prepend API_ROOT to url (unless absolute url passed in)
// - set some default init values with fetchInit and merge in passed inits
// - calls response.json() and returns a promise
// - use by chaining your own `then` and `catch` methods
// - our 'api' goal for these methods:
//    - `then` will always be a 20x response as json
//    - `catch` will always be an APIError instance
export function get(url, init = {}) {
    return doFetch(apiUrl(url), fetchInit(init));
}

export function post(url, data = {}, init = {}) {
    return doFetch(
        apiUrl(url),
        fetchInit({
            method: 'POST',
            body: JSON.stringify(data),
            ...init,
        })
    );
}

export function postForm(url, body = FormData(), init = {}) {
    return doFetch(
        apiUrl(url),
        fetchInit({
            method: 'POST',
            body,
            ...init
        })
    );
}

export function patch(url, data = {}, init = {}) {
    return doFetch(
        apiUrl(url),
        fetchInit({
            method: 'PATCH',
            body: JSON.stringify(data),
            ...init,
        })
    );
}

export function options(url, init = {}) {
    return doFetch(
        apiUrl(url),
        fetchInit({
            method: 'OPTIONS',
            ...init,
        })
    );
}

export function _delete(url, init = {}) {
    return doFetch(
        apiUrl(url),
        fetchInit({
            method: 'DELETE',
            ...init,
        })
    );
}

export function extractNameFromDataUri(dataUri) {
    return decodeURIComponent(dataUri.match(/name=(.*);/)[1] || 'File');
}

export function uploadFile(dataUri, init = {}) {
    const {
        name = '',
        apiUrl: passedApiUrl = 'files/',
        ...postInit
    } = init;

    const data = {
        file: dataUri,
        name: name || extractNameFromDataUri(dataUri),
    };

    return post(apiUrl(passedApiUrl), data, postInit);
}

export function download(url, init = {}) {
    /* Confusing name but here's what this assumes:
     * - our api returns `{url: '...'}` with signed, expiring urls to protected resources on cdn
     * - we pass the url to *that* request here, which then unpacks it and points browser at resp.url
     * - Yeah, this is awkward. Why not just follow the redirect using fetch? As 2018, at least, this
     *   is a mess. We control both api and client here, so let's just make our lives easy.
     */
    return get(url, init).then(json => {
        if (json.url) {
            window.location = json.url;
        } else {
            return json;
        }
    });
}


export const downloadBlob = (url, headers = {}, callbacks = {}) => {
    /*
     * So `download` cannot be used everywhere: it assumes we get a URL that
     * makes a request to our API which returns a url to an asset on s3
     * that we can then redirect to.
     *
     * But what if we have a URL to a PDF and we just want to download it?
     * We still have to make the request as an ajax request through
     * our API (that's where our credentials are) but we need to handle
     * the response properly, which...is a pain. So we're going to skip
     * our API fetch wrappers and handle this with the raw fetch API.
     *
     * Important: callbacks.done is passed the object url from URL.createObjectURL
     * It's up to you to attach it to a button or something like that to actually
     * trigger the download.
     *
     * You can just do this:
     *     ...
     *     done: href => window.location.href,
     *     ...
     *
     * But you may want more control. (i.e. filename)
    **/
    const { start, done, fail } = callbacks;

    const init = addHeaders({
        method: 'GET',
        mode: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            ...headers
        },
    });

    start && start();
    fetch(url, init)
        .then(resp => {
            if (!resp.ok) {
                throw Error(resp.statusText);
            }
            return resp.blob();
        })
        .then(blob => {
            const href = window.URL.createObjectURL(blob);
            done(href);
        })
        .catch(error => {
            fail && fail(error);
        });
};


export const addHeaders = (init = {}, getHeaders) => {
    const headers = init.headers || {};
    const newHeaders = getHeaders ? getHeaders() : {};
    return { ...init, headers: { ...headers, ...newHeaders } };
};

const api = {
    apiUrl,
    api_root: API_ROOT,
    get,
    post,
    postForm,
    patch,
    options,
    delete: _delete,
    download,
    uploadFile,
};

export default api;
